/* CookieConsentBanner
 *
 * Bottom-of-screen GDPR/PECR-friendly consent banner.
 * - Stores the user's choice in localStorage under the key
 *   `hyredocs_cookie_consent` (matches the row in /cookies).
 * - Strictly-necessary cookies are always on (auth, consent storage).
 * - Optional analytics defaults to OFF until the user accepts. We
 *   don't actually load analytics today — the consent value is wired
 *   so that when we add Plausible/PostHog we can gate the loader on
 *   `consent.analytics === true` without revisiting this component.
 * - Listens for `hyredocs:open-cookie-preferences` so the /cookies
 *   page (and any other surface) can re-open the preference panel.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'hyredocs_cookie_consent';
const COOKIE_VERSION = 1; // Bump if the categories change so users get re-prompted.

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== COOKIE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, v: COOKIE_VERSION, at: new Date().toISOString() }));
  } catch {
    /* localStorage blocked — silently no-op, user will be prompted again */
  }
}

export default function CookieConsentBanner() {
  // Compute initial state lazily so we don't need to setState inside
  // useEffect on mount (which React 19's lint rules complain about).
  const [visible, setVisible] = useState(() => readConsent() === null);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(() => !!readConsent()?.analytics);

  // Subscribe to the "open preferences" custom event so the /cookies
  // page (and any other surface) can re-open the panel.
  useEffect(() => {
    const onOpen = () => {
      const cur = readConsent();
      setAnalytics(!!cur?.analytics);
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener('hyredocs:open-cookie-preferences', onOpen);
    return () => window.removeEventListener('hyredocs:open-cookie-preferences', onOpen);
  }, []);

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true });
    setVisible(false);
  };

  const rejectOptional = () => {
    writeConsent({ necessary: true, analytics: false });
    setVisible(false);
  };

  const savePreferences = () => {
    writeConsent({ necessary: true, analytics });
    setVisible(false);
    setExpanded(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-base">Cookies, briefly</h3>
            <p className="text-sm text-slate-300 mt-1 leading-relaxed">
              We use strictly-necessary cookies to keep you signed in and, with your consent, analytics cookies to improve the product. We never use advertising cookies.{' '}
              <Link to="/cookies" className="text-emerald-300 underline underline-offset-2">Learn more</Link>.
            </p>

            {expanded && (
              <div className="mt-4 space-y-2.5 text-sm border-t border-slate-700 pt-3" data-testid="cookie-preferences">
                <label className="flex items-start gap-2.5 opacity-70 cursor-not-allowed">
                  <input type="checkbox" checked readOnly className="mt-1" />
                  <span>
                    <span className="font-semibold">Strictly necessary</span>
                    <span className="block text-xs text-slate-400">Sign-in, session, and consent storage. Always on.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-1"
                    data-testid="cookie-toggle-analytics"
                  />
                  <span>
                    <span className="font-semibold">Analytics</span>
                    <span className="block text-xs text-slate-400">Privacy-friendly page-view stats. No cross-site tracking.</span>
                  </span>
                </label>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {!expanded && (
                <>
                  <Button
                    onClick={acceptAll}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold flex-1"
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </Button>
                  <Button
                    onClick={rejectOptional}
                    variant="outline"
                    className="bg-transparent border-slate-600 text-white hover:bg-white/5 hover:text-white flex-1"
                    data-testid="cookie-reject-optional"
                  >
                    Reject optional
                  </Button>
                  <Button
                    onClick={() => setExpanded(true)}
                    variant="ghost"
                    className="w-full text-slate-300 hover:text-white hover:bg-white/5 text-xs"
                    data-testid="cookie-preferences-toggle"
                  >
                    Manage preferences
                  </Button>
                </>
              )}
              {expanded && (
                <Button
                  onClick={savePreferences}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold w-full"
                  data-testid="cookie-save-prefs"
                >
                  Save preferences
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
